package com.eladfinish.bible.contest.features.deeplink

import com.eladfinish.bible.contest.appcontent.AppLinks
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetEntryPoint
import com.eladfinish.bible.contest.navigation.BrowseScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.NavigationDestination
import com.eladfinish.bible.contest.navigation.PastQuizzesNavigationDestination
import com.eladfinish.bible.contest.navigation.SearchScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.VerseWidgetScreenNavigationDestination
import com.eladfinish.bible.contest.screens.pastQuizzes.toDuelCodeOrNull
import net.thauvin.erik.urlencoder.UrlEncoderUtil

object DeepLinks {

    const val HOST = AppLinks.LANDING_HOST

    /** The short host the site's invitations use; [HOST] keeps working for links sent before it. */
    const val DUEL_HOST = AppLinks.SHORT_HOST

    /** Both serve the same pages and the same .well-known files. */
    private val HOSTS = listOf(HOST, DUEL_HOST)
    const val SEARCH_PATH = "/search"
    const val SEARCH_QUERY_PARAM = ScreenSlugs.SEARCH_QUERY_PARAM

    /** A ראש בראש room: `/duel/{code}` (the page's invitation; ExamDuel.kt). */
    const val DUEL_PATH = "/duel/"

    /**
     * An inner screen: `/open/{slug}`, maybe with the screen's query (`/open/search?q=…`), by the
     * slugs of [ScreenSlugs]. Without the app, the landing page (open.html) sends the browser to the
     * same screen of the web app, `/app/#{slug}?{query}`.
     */
    const val OPEN_PATH = "/open/"

    fun buildSearchLink(expression: String): String =
        "https://$HOST$SEARCH_PATH?$SEARCH_QUERY_PARAM=${UrlEncoderUtil.encode(expression)}"

    fun buildDuelLink(code: String): String = "https://$DUEL_HOST$DUEL_PATH$code"

    /**
     * Opens the chapter of a verse, from the rotating verse widget. It has a scheme of its own, not the
     * site's host, because a widget tap must open the app and nothing else, and the site's links need the
     * domain verification of each store to do that.
     */
    private const val VERSE_LINK_PREFIX = "biblecontest://verse?"

    /** [bookNumber] is the 1-based number of the book in the Tanakh (28 is Mishlei). */
    fun buildVerseLink(bookNumber: Int, chapter: Int, verse: Int): String =
        "${VERSE_LINK_PREFIX}book=$bookNumber&chapter=$chapter&verse=$verse"

    /** The widget's gear: opens the widget's settings screen. */
    private const val VERSE_WIDGET_SETTINGS_LINK = "biblecontest://verse-widget"

    fun buildVerseWidgetSettingsLink(): String = VERSE_WIDGET_SETTINGS_LINK

    /** The link to [destination]'s screen, or `null` when [ScreenSlugs] has no slug for it. */
    fun buildScreenLink(destination: NavigationDestination): String? {
        val slug = ScreenSlugs.destinationsBySlug.entries.firstOrNull { it.value::class == destination::class }?.key
            ?: return null
        val query = ScreenSlugs.queryFor(destination)
        return "https://$HOST$OPEN_PATH$slug" + if (query != null) "?$query" else ""
    }

    fun parse(url: String): NavigationDestination? {
        if (url.startsWith(VERSE_LINK_PREFIX)) return parseVerse(url.removePrefix(VERSE_LINK_PREFIX))
        if (url == VERSE_WIDGET_SETTINGS_LINK) return VerseWidgetScreenNavigationDestination(VerseWidgetEntryPoint.WIDGET_GEAR)

        val withoutScheme = url
            .removePrefix("https://")
            .removePrefix("http://")

        val host = HOSTS.firstOrNull { withoutScheme.startsWith(it) } ?: return null

        val pathAndQuery = withoutScheme.removePrefix(host)
        if (pathAndQuery.startsWith(DUEL_PATH)) return parseDuel(pathAndQuery.removePrefix(DUEL_PATH))
        if (pathAndQuery.startsWith(OPEN_PATH)) return parseScreen(pathAndQuery.removePrefix(OPEN_PATH))
        if (pathAndQuery != SEARCH_PATH && !pathAndQuery.startsWith("$SEARCH_PATH?")) return null

        val query = pathAndQuery.substringAfter('?', missingDelimiterValue = "")
        val expression = ScreenSlugs.queryParam(query, SEARCH_QUERY_PARAM).orEmpty()

        return SearchScreenNavigationDestination(text = expression)
    }

    private fun parseVerse(query: String): NavigationDestination? {
        val params = query.substringBefore('#').split('&').associate {
            it.substringBefore('=') to it.substringAfter('=', missingDelimiterValue = "")
        }
        val book = params["book"]?.toIntOrNull()?.takeIf { it in 1..39 } ?: return null
        val chapter = params["chapter"]?.toIntOrNull()?.takeIf { it >= 1 } ?: return null
        val verse = params["verse"]?.toIntOrNull()?.takeIf { it >= 1 } ?: 0
        // The 0-based book and the 1-based chapter, as the random verse screen opens a chapter
        return BrowseScreenNavigationDestination(
            fromDialog = true,
            bookIndex = book - 1,
            chapterIndex = chapter,
            verse = verse,
        )
    }

    /**
     * `{slug}`, maybe followed by `/`, a query or a fragment. The root screen is no destination: the
     * app opens on it anyway, and navigating there would stack a second one.
     */
    private fun parseScreen(rest: String): NavigationDestination? {
        val slug = rest.substringBefore('?').substringBefore('#').removeSuffix("/")
        if (slug == ScreenSlugs.ROOT) return null
        val query = rest.substringAfter('?', missingDelimiterValue = "").substringBefore('#')
        return ScreenSlugs.destinationFor(slug, query)
    }

    /** `{code}`, maybe followed by `/`, a query or a fragment (what a messaging app may add). */
    private fun parseDuel(rest: String): NavigationDestination? {
        val code = rest.substringBefore('?').substringBefore('#').removeSuffix("/").toDuelCodeOrNull() ?: return null
        return PastQuizzesNavigationDestination(duelCode = code)
    }
}
